import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneRight, Trash, Clock } from "@phosphor-icons/react";

export default function ForumThread() {
  const { tid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/forum/threads/${tid}`);
      setThread(r.data);
    } catch (e) {
      toast.error("Thread not found");
      navigate("/app/forum");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);

  const post = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      await api.post(`/forum/threads/${tid}/replies`, { body: reply });
      setReply("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPosting(false); }
  };

  const deleteThread = async () => {
    if (!window.confirm("Delete this thread and all replies?")) return;
    try {
      await api.delete(`/forum/threads/${tid}`);
      toast.success("Thread deleted");
      navigate("/app/forum");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!thread) return <div className="text-sm text-zinc-500">Loading…</div>;

  const canDelete = user && (user.user_id === thread.author_id || ["institution_admin", "super_admin"].includes(user.role));

  return (
    <div>
      <Link to="/app/forum" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 hover:text-zinc-900 mb-4">
        <ArrowLeft size={12} /> Back to forum
      </Link>

      <div className="border border-zinc-200 p-8 mb-6" data-testid="forum-thread-detail">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
              {thread.category?.replace("_", " ")}
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="thread-title">{thread.title}</h1>
          </div>
          {canDelete && (
            <button
              onClick={deleteThread}
              className="text-red-600 hover:text-red-700 border border-red-200 hover:border-red-600 px-3 py-2 text-xs inline-flex items-center gap-1.5"
              data-testid="thread-delete-btn"
            >
              <Trash size={12} /> Delete
            </button>
          )}
        </div>
        <div className="text-xs text-zinc-500 mb-6 flex items-center gap-3">
          <span>by <span className="text-zinc-900 font-medium">{thread.author_name}</span> · <span className="uppercase tracking-wider text-[10px]">{thread.author_role?.replace("_", " ")}</span></span>
          <span className="text-zinc-300">·</span>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {new Date(thread.created_at).toLocaleString()}</span>
          <span className="text-zinc-300">·</span>
          <span>{thread.views || 0} views</span>
        </div>
        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-zinc-800" data-testid="thread-body">
          {thread.body}
        </div>
      </div>

      {/* Replies */}
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-3">
          {thread.reply_count || 0} {thread.reply_count === 1 ? "reply" : "replies"}
        </div>
        <div className="space-y-3" data-testid="reply-list">
          {(thread.replies || []).map((r) => (
            <div key={r.reply_id} className="border border-zinc-200 p-5" data-testid={`reply-${r.reply_id}`}>
              <div className="flex items-center gap-3 mb-3 text-xs text-zinc-500">
                <span className="text-zinc-900 font-medium">{r.author_name}</span>
                <span className="uppercase tracking-wider text-[10px] border border-zinc-300 px-2 py-0.5">{r.author_role?.replace("_", " ")}</span>
                <span className="text-zinc-300">·</span>
                <span>{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-zinc-800 whitespace-pre-wrap">{r.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Reply box */}
      <div className="border border-zinc-200 p-5" data-testid="reply-form">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          placeholder="Write a reply…"
          className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none mb-3"
          data-testid="reply-body-input"
        />
        <div className="flex justify-end">
          <button
            onClick={post}
            disabled={posting || !reply.trim()}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
            data-testid="reply-submit-btn"
          >
            <PaperPlaneRight size={13} weight="fill" /> {posting ? "Posting…" : "Post reply"}
          </button>
        </div>
      </div>
    </div>
  );
}
